import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'

interface SystemStats {
  totalDashboards: number
  activeDashboards: number
  totalComponents: number
  activeComponents: number
  totalVisits: number
  uniqueVisitors: number
  totalRecords: number
}

export function SystemStatsCard() {
  const [stats, setStats] = useState<SystemStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)

        // Obtener dashboards
        const { count: totalDashboards } = await supabase
          .from('dashboards')
          .select('*', { count: 'exact', head: true })

        const { count: activeDashboards } = await supabase
          .from('dashboards')
          .select('*', { count: 'exact', head: true })
          .eq('is_published', true)

        // Obtener componentes
        const { count: totalComponents } = await supabase
          .from('components')
          .select('*', { count: 'exact', head: true })

        const { count: activeComponents } = await supabase
          .from('components')
          .select('*', { count: 'exact', head: true })
          .eq('is_published', true)

        // Obtener visitas totales
        const { count: totalVisits } = await supabase
          .from('dashboard_views')
          .select('*', { count: 'exact', head: true })

        // Obtener visitantes únicos (contar session_id únicos)
        const { data: uniqueSessionsData } = await supabase
          .from('dashboard_views')
          .select('session_id')

        const uniqueVisitors = uniqueSessionsData
          ? new Set(uniqueSessionsData.map((v: any) => v.session_id)).size
          : 0

        // Obtener cantidad de registros de datos (existencias_forestales)
        const { count: totalRecords } = await supabase
          .from('existencias_forestales')
          .select('*', { count: 'exact', head: true })

        setStats({
          totalDashboards: totalDashboards || 0,
          activeDashboards: activeDashboards || 0,
          totalComponents: totalComponents || 0,
          activeComponents: activeComponents || 0,
          totalVisits: totalVisits || 0,
          uniqueVisitors,
          totalRecords: totalRecords || 0,
        })
      } catch (error) {
        console.error('Error fetching system stats:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
        <p className="text-red-700">Error al cargar las estadísticas del sistema</p>
      </div>
    )
  }

  const statCards = [
    {
      title: 'Visitas Totales',
      value: stats.totalVisits.toLocaleString('es-ES'),
      subtitle: `${stats.uniqueVisitors.toLocaleString('es-ES')} visitantes únicos`,
      icon: '👁️',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
    },
    {
      title: 'Dashboards',
      value: stats.activeDashboards.toString(),
      subtitle: `${stats.totalDashboards} totales (${stats.activeDashboards} activos)`,
      icon: '📊',
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
    },
    {
      title: 'Componentes',
      value: stats.activeComponents.toString(),
      subtitle: `${stats.totalComponents} totales (${stats.activeComponents} activos)`,
      icon: '🧩',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
    },
    {
      title: 'Registros de Datos',
      value: stats.totalRecords.toLocaleString('es-ES'),
      subtitle: 'Existencias forestales',
      icon: '📁',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700',
    },
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {statCards.map((card, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
        >
          <div className={`h-2 bg-gradient-to-r ${card.color}`}></div>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                {card.title}
              </h3>
              <span className="text-3xl">{card.icon}</span>
            </div>
            <div className="mb-2">
              <p className="text-4xl font-bold text-gray-900">{card.value}</p>
            </div>
            <p className="text-sm text-gray-500">{card.subtitle}</p>
          </div>
        </div>
      ))}
    </div>
  )
}
